import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../../config/ConfigContext';
import useMediaQuery from '../../hooks/useMediaQuery';
import VideoCard from '../VideoCard';

/**
 * VideoHighlights Component
 * Shows popular videos and recent lives in responsive layout
 * Desktop: Horizontal scrolling rows
 * Mobile: 2x2 grid
 */
const VideoHighlights = ({ popularVideos = [], recentLives = [], loading = false }) => {
  const { theme } = useConfig();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const popularRowRef = useRef(null);
  const livesRowRef = useRef(null);
  
  // Limit items based on device
  const displayPopular = isMobile ? popularVideos.slice(0, 4) : popularVideos.slice(0, 8);  // 4 for mobile to match shorts
  const displayLives = isMobile ? recentLives.slice(0, 4) : recentLives.slice(0, 8);
  
  // Don't render if no content
  if (loading || (displayPopular.length === 0 && displayLives.length === 0)) {
    return null;
  }
  
  // Scroll functions for desktop
  const scrollRow = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 320; // Card width + gap
      ref.current.scrollBy({ 
        left: direction === 'right' ? scrollAmount : -scrollAmount, 
        behavior: 'smooth' 
      });
    }
  };
  
  const styles = {
    section: {
      marginTop: '0',
      marginBottom: '52px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    title: {
      fontSize: isMobile ? '18px' : '20px',
      fontWeight: '600',
      margin: 0,
      color: theme?.colors?.headingColor || theme?.colors?.primary || '#0f172a'
    },
    viewAll: {
      color: theme?.colors?.linkColor || theme?.colors?.primary || '#0f172a',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    container: {
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)',
      borderRadius: isMobile ? '12px' : '16px',
      boxShadow: isMobile ? '0 4px 20px rgba(0, 0, 0, 0.06)' : '0 10px 40px rgba(0, 0, 0, 0.08)',
      padding: isMobile ? '8px' : '16px',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.6)',
      position: 'relative',
      overflow: 'hidden'
    },
    mobileGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: '8px',
      marginBottom: '12px'
    },
    desktopRow: {
      position: 'relative',
      marginBottom: '32px',
      padding: '0 20px'
    },
    scrollContainer: {
      display: 'flex',
      overflowX: 'auto',
      gap: '12px',
      padding: '8px 4px',
      scrollBehavior: 'smooth',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      WebkitScrollbar: 'none'
    },
    scrollButton: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.95)',
      border: '1px solid rgba(0, 0, 0, 0.08)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      zIndex: 2,
      transition: 'all 0.3s ease'
    },
    cardWrapper: {
      flex: '0 0 340px',
      minWidth: '340px',
      maxWidth: '340px'
    }
  };
  
  return (
    <div style={styles.section}>
      {/* Popular Videos Section */}
      {displayPopular.length > 0 && (
        <>
          <div style={styles.header}>
            <div>
              <h2 style={styles.title}>Videos</h2>
              <p style={{
                margin: '2px 0 0 0',
                fontSize: isMobile ? '12px' : '14px',
                color: theme?.colors?.darkGray || '#666',
                fontWeight: '400'
              }}>Recommended for you</p>
            </div>
            <Link to="/videos" style={styles.viewAll}>
              View all
              <svg width="16" height="16" viewBox="0 0 24 24" fill={theme.colors.linkColor || theme.colors.primary}>
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
              </svg>
            </Link>
          </div>
          
          <div style={styles.container}>
            {isMobile ? (
              // Mobile: 2x2 Grid
              <div style={styles.mobileGrid}>
                {displayPopular.map((video, index) => (
                  <VideoCard 
                    key={video.id || index}
                    video={video}
                    isMobileGrid={true}
                  />
                ))}
              </div>
            ) : (
              // Desktop: Horizontal Scroll
              <div style={styles.desktopRow}>
                {/* Left Scroll Button */}
                {displayPopular.length > 4 && (
                  <button
                    onClick={() => scrollRow(popularRowRef, 'left')}
                    style={{ ...styles.scrollButton, left: 0 }}
                    aria-label="Scroll left"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                    </svg>
                  </button>
                )}
                
                {/* Video Cards */}
                <div ref={popularRowRef} style={styles.scrollContainer}>
                  {displayPopular.map((video, index) => (
                    <div key={video.id || index} style={styles.cardWrapper}>
                      <VideoCard video={video} />
                    </div>
                  ))}
                </div>
                
                {/* Right Scroll Button */}
                {displayPopular.length > 4 && (
                  <button
                    onClick={() => scrollRow(popularRowRef, 'right')}
                    style={{ ...styles.scrollButton, right: 0 }}
                    aria-label="Scroll right"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}
      
      {/* Recent Lives Section */}
      {displayLives.length > 0 && (
        <>
          <div style={{ ...styles.header, marginTop: '32px' }}>
            <div>
              <h2 style={styles.title}>Live Videos</h2>
              <p style={{
                margin: '2px 0 0 0',
                fontSize: isMobile ? '12px' : '14px',
                color: theme?.colors?.darkGray || '#666',
                fontWeight: '400'
              }}>Recent broadcasts</p>
            </div>
            <Link to="/videos?type=live" style={styles.viewAll}>
              View all
              <svg width="16" height="16" viewBox="0 0 24 24" fill={theme.colors.linkColor || theme.colors.primary}>
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
              </svg>
            </Link>
          </div>
          
          <div style={styles.container}>
            {isMobile ? (
              // Mobile: 2x2 Grid
              <div style={styles.mobileGrid}>
                {displayLives.map((video, index) => (
                  <VideoCard 
                    key={video.id || index}
                    video={video}
                    isLive={true}
                    isMobileGrid={true}
                  />
                ))}
              </div>
            ) : (
              // Desktop: Horizontal Scroll
              <div style={styles.desktopRow}>
                {/* Left Scroll Button */}
                {displayLives.length > 4 && (
                  <button
                    onClick={() => scrollRow(livesRowRef, 'left')}
                    style={{ ...styles.scrollButton, left: 0 }}
                    aria-label="Scroll left"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                    </svg>
                  </button>
                )}
                
                {/* Video Cards */}
                <div ref={livesRowRef} style={styles.scrollContainer}>
                  {displayLives.map((video, index) => (
                    <div key={video.id || index} style={styles.cardWrapper}>
                      <VideoCard video={video} isLive={true} />
                    </div>
                  ))}
                </div>
                
                {/* Right Scroll Button */}
                {displayLives.length > 4 && (
                  <button
                    onClick={() => scrollRow(livesRowRef, 'right')}
                    style={{ ...styles.scrollButton, right: 0 }}
                    aria-label="Scroll right"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default VideoHighlights;